import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AgeVerification from '@/components/feature/AgeVerification';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  const allCategoryArticles = useMemo(
    () => (activeSlug === 'all' ? articles : categoryArticles),
    [activeSlug, categoryArticles],
  );

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <AgeVerification />
      <Navbar />

      <main className="pt-20">
        <section className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mt-4 mb-2">
              漫画风格分类
            </h1>
            <p className="text-sm text-foreground-500">
              探索八大成人漫画核心分类，发现属于你的阅读风格
            </p>
          </div>
        </section>

        <section className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 p-5 rounded-lg bg-background-100 border border-background-200/60">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/15 flex-shrink-0">
                  <i className={`${currentCategory.icon} text-accent-500 text-lg`}></i>
                </span>
                <div>
                  <h2 className="text-lg font-bold font-heading text-foreground-950 mb-1">
                    {currentCategory.name}
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {currentCategory.description}
                  </p>
                  <p className="text-xs text-foreground-400 mt-2">
                    共 {currentCategory.articleCount} 篇相关文章
                  </p>
                </div>
              </div>
            </div>

            {allCategoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {allCategoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/50">
                  <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无相关文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}